import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { hideModal } from '../../../actions/modal.actions';
import {
	getAccountServicelines,
	createDiscount,
	updateDiscount,
	emptyAccountServicelines,
	getServiceLineDiscounts,
	getAccountDiscount,
} from '../../../actions/accountDetailsActions/billing.actions';
import { getAccountDetails, getUser, getModalData } from '../../../selectors';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toastr } from 'react-redux-toastr';

const toDateInputValue = (val) => {
	if (!val) return '';
	const m = moment.utc(val);
	return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

class AddAccountDiscountModal extends Component {
	state = {
		showAlertMessage: false,
		alertMessage: '',
		catalogDiscounts: [],
		discountsLoading: false,
		selectedDiscount: null,
		editServiceLineId: null,
	};

	componentDidMount() {
		this.props.getAccountServicelines(this.props.accountId);

		const isEdit = this.props.modalProps?.type === 'edit';
		if (isEdit && this.props.modalProps?.row?.id) {
			this.props.getAccountDiscount(this.props.accountId, this.props.modalProps.row.id).then((response) => {
				const accountDiscount = response?.data || null;
				this.setState({
					selectedDiscount: accountDiscount?.discount || accountDiscount,
					editServiceLineId: accountDiscount?.serviceLineId || null,
				});
			});
		}

		const preselectedServiceLineId = !isEdit && this.props.modalProps?.serviceLineId;
		if (preselectedServiceLineId) {
			this.setState({ discountsLoading: true });
			this.props.getServiceLineDiscounts(this.props.accountId, preselectedServiceLineId).then((response) => {
				const discounts = response?.data?.discounts || response?.data || [];
				const sorted = Array.isArray(discounts)
					? [...discounts].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
					: [];
				this.setState({ catalogDiscounts: sorted, discountsLoading: false });
			}).catch(() => {
				this.setState({ discountsLoading: false });
			});
		}
	}

	componentWillUnmount() {
		this.props.emptyAccountServicelines();
	}

	formSchema = () => {
		const { selectedDiscount } = this.state;
		const isEdit = this.props.modalProps?.type === 'edit';

		const dateFields = {
			validFrom: Yup.string().required('Required')
				.test('within-discount-start', '', function(validFrom) {
					if (!validFrom || !selectedDiscount?.startDate) return true;
					if (new Date(validFrom) < new Date(selectedDiscount.startDate)) {
						return this.createError({
							message: `Must be on or after ${moment(selectedDiscount.startDate).format('MM/DD/YYYY')}`,
						});
					}
					return true;
				})
				.test('before-end', 'Must be before end date', function(validFrom) {
					const { validTo } = this.parent;
					if (!validFrom || !validTo) return true;
					return new Date(validFrom) <= new Date(validTo);
				}),
			validTo: Yup.string().required('Required')
				.test('within-discount-end', '', function(validTo) {
					if (!validTo || !selectedDiscount?.endDate) return true;
					if (new Date(validTo) > new Date(selectedDiscount.endDate)) {
						return this.createError({
							message: `Must be on or before ${moment(selectedDiscount.endDate).format('MM/DD/YYYY')}`,
						});
					}
					return true;
				})
				.test('after-start', 'Must be equal to or after Valid From', function(validTo) {
					const { validFrom } = this.parent;
					if (!validFrom || !validTo) return true;
					return new Date(validTo) >= new Date(validFrom);
				}),
		};

		if (isEdit) {
			return Yup.object().shape(dateFields);
		}

		return Yup.object().shape({
			discountId: Yup.string().required('Required'),
			serviceLineId: Yup.string().required('Required'),
			applyOn: Yup.string().required('Required'),
			...dateFields,
		});
	};

	onServiceLineChange = (serviceLineId, setFieldValue) => {
		setFieldValue('serviceLineId', serviceLineId);
		setFieldValue('discountId', '');
		setFieldValue('validFrom', '');
		setFieldValue('validTo', '');
		this.setState({ selectedDiscount: null });
		if (!serviceLineId) {
			this.setState({ catalogDiscounts: [], discountsLoading: false });
			return;
		}
		this.setState({ catalogDiscounts: [], discountsLoading: true });
		this.props.getServiceLineDiscounts(this.props.accountId, serviceLineId).then((response) => {
			const discounts = response?.data?.discounts || response?.data || [];
			const sorted = Array.isArray(discounts)
				? [...discounts].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
				: [];
			this.setState({ catalogDiscounts: sorted, discountsLoading: false });
		}).catch(() => {
			this.setState({ discountsLoading: false });
		});
	};

	onDiscountChange = (discountId, setFieldValue) => {
		setFieldValue('discountId', discountId);
		const selected = this.state.catalogDiscounts.find((d) => d.id.toString() === discountId) || null;
		this.setState({ selectedDiscount: selected });

		if (selected) {
			setFieldValue('validFrom', moment().format('YYYY-MM-DD'));
			setFieldValue('validTo', moment()
				.add(selected.validDays ? selected.validDays : 0, 'days')
				.add(selected.validMonths ? selected.validMonths : 0, 'months')
				.format('YYYY-MM-DD'));
		} else {
			setFieldValue('validFrom', '');
			setFieldValue('validTo', '');
		}
	};

	formSubmit = (values, actions) => {
		const isEdit = this.props.modalProps.type === 'edit';
		const payload = isEdit
			? { validFrom: values.validFrom, validTo: values.validTo, serviceLineId: this.state.editServiceLineId }
			: {
				discountId: values.discountId,
				locationId: this.props.currentLocationId,
				serviceLineId: values.serviceLineId,
				applyOn: values.applyOn,
				validFrom: values.validFrom,
				validTo: values.validTo,
				isService: true,
			};

		if (this.props.modalProps.type === 'create') {
			this.props.createDiscount(this.props.accountId, payload).then((response) => {
				if (response.data.success) {
					toastr.success(response.data.message, { timeOut: 1000, position: 'top-center' });
					this.closeModal();
				} else {
					const errorMessage = response.data.error?.code + ': ' + response.data.error?.message;
					toastr.error(errorMessage, { timeOut: 0, position: 'top-center' });
					actions.setSubmitting(false);
					this.setState({
						showAlertMessage: true,
						alertMessage: response.data.error?.message,
					});
				}
			});
		} else {
			const { accountId, modalProps } = this.props;
			this.props.updateDiscount(accountId, modalProps.row.id, payload).then((response) => {
				if (response.data.success) {
					toastr.success(response.data.message, { timeOut: 1000, position: 'top-center' });
					this.closeModal();
				} else {
					const errorMessage = response.data.error?.code + ': ' + response.data.error?.message;
					toastr.error(errorMessage, { timeOut: 0, position: 'top-center' });
					actions.setSubmitting(false);
				}
			});
		}
	};

	closeModal = () => {
		this.props.hideModal();
		if (this.props.modalProps.onCloseCallback) {
			this.props.modalProps.onCloseCallback();
		}
	};

	render() {
		const { showAlertMessage, alertMessage, catalogDiscounts, discountsLoading, selectedDiscount } = this.state;
		const { modalProps, discountServicelinesData } = this.props;
		const isEdit = modalProps.type === 'edit';

		const discountDateHint = selectedDiscount
			? [
				selectedDiscount.startDate ? `from ${moment(selectedDiscount.startDate).format('MM/DD/YYYY')}` : null,
				selectedDiscount.endDate   ? `to ${moment(selectedDiscount.endDate).format('MM/DD/YYYY')}`     : null,
			  ].filter(Boolean).join(' ')
			: null;

		return (
			<Fragment>
				<div className="modal" style={{ display: 'block' }} tabIndex="-1" role="dialog">
					<Formik
						initialValues={{
							discountId:   (modalProps.row && modalProps.row.discountId)   || '',
							serviceLineId:(modalProps.row && modalProps.row.serviceLineId) || modalProps.serviceLineId || '',
							applyOn:      modalProps.applyOnPeriod || 'immediately',
							validFrom:    toDateInputValue(modalProps.row && modalProps.row.validFrom),
							validTo:      toDateInputValue(modalProps.row && modalProps.row.validTo),
						}}
						validationSchema={this.formSchema()}
						onSubmit={this.formSubmit}
						render={({ setFieldValue, handleChange, handleSubmit, handleBlur, values, errors, touched, isSubmitting }) => (
							<form onSubmit={handleSubmit}>
								<div className="modal-dialog">
									<div className="modal-content">
										<div className="modal-header">
											<h5 className="modal-title">
												{modalProps.type === 'create' ? 'Add' : 'Edit'} Account Discount
											</h5>
											<button onClick={this.closeModal} type="button" className="close">
												<span aria-hidden="true">&times;</span>
											</button>
										</div>
										<div className="modal-body form-horizontal">

											{showAlertMessage && (
												<div className="alert alert-inline alert-danger alert-dismissible">
													<p className="mb-0">{alertMessage}</p>
												</div>
											)}

											{isEdit ? (
												<>
													<div className="form-group">
														<label className="font-weight-bold">Serviceline</label>
														<p className="form-control-plaintext">{modalProps.row.serviceLineNumber}</p>
													</div>
													<div className="form-group">
														<label className="font-weight-bold">Discount</label>
														<p className="form-control-plaintext">{modalProps.row.discountDescription}</p>
													</div>
												</>
											) : (
												<>
													<div className="form-group">
														<label className="font-weight-bold">Serviceline</label>
														<select
															className={'form-control' + (errors.serviceLineId && touched.serviceLineId ? ' is-invalid' : '')}
															name="serviceLineId"
															value={values.serviceLineId}
															onChange={(e) => this.onServiceLineChange(e.target.value, setFieldValue)}
															onBlur={handleBlur}
														>
															<option value="">Please select serviceline</option>
															{discountServicelinesData.map((serviceline) => (
																<option value={serviceline.id} key={serviceline.id}>
																	{serviceline.number}
																</option>
															))}
														</select>
														{errors.serviceLineId && touched.serviceLineId && (
															<div className="invalid-feedback">{errors.serviceLineId}</div>
														)}
													</div>
													<div className="form-group">
														<label className="font-weight-bold">Discount</label>
														<select
															className={'form-control' + (errors.discountId && touched.discountId ? ' is-invalid' : '')}
															name="discountId"
															value={values.discountId}
															onChange={(e) => this.onDiscountChange(e.target.value, setFieldValue)}
															onBlur={handleBlur}
															disabled={!values.serviceLineId || discountsLoading || catalogDiscounts.length === 0}
														>
															<option value="">
																{!values.serviceLineId ? 'Select a serviceline first' : discountsLoading ? 'Loading...' : catalogDiscounts.length === 0 ? 'No Available Discounts to select' : 'Please select a discount'}
															</option>
															{catalogDiscounts.map((d) => (
																<option value={d.id.toString()} key={d.id}>
																	{d.description} ({d.name})
																</option>
															))}
														</select>
														{errors.discountId && touched.discountId && (
															<div className="invalid-feedback">{errors.discountId}</div>
														)}
													</div>
												</>
											)}

											<div className="form-group">
												<label className="font-weight-bold">Valid From</label>
												<input
													type="date"
													className={'form-control' + (errors.validFrom && touched.validFrom ? ' is-invalid' : '')}
													name="validFrom"
													value={values.validFrom}
													onChange={handleChange}
													onBlur={handleBlur}
													disabled={!isEdit && !values.discountId}
												/>
												{errors.validFrom && touched.validFrom && (
													<div className="invalid-feedback">{errors.validFrom}</div>
												)}
											</div>

											<div className="form-group">
												<label className="font-weight-bold">Valid To</label>
												<input
													type="date"
													className={'form-control' + (errors.validTo && touched.validTo ? ' is-invalid' : '')}
													name="validTo"
													value={values.validTo}
													onChange={handleChange}
													onBlur={handleBlur}
													disabled={!isEdit && !values.discountId}
												/>
												{errors.validTo && touched.validTo && (
													<div className="invalid-feedback">{errors.validTo}</div>
												)}
												{discountDateHint && (
													<small className="text-muted">Discount is valid {discountDateHint}</small>
												)}
												{isEdit && (
													<small className="text-muted d-block">
														To deactivate this discount on this account set the Valid To to a date in the past or today’s date.
													</small>
												)}
											</div>

										</div>
										<div className="modal-footer">
											<button onClick={this.closeModal} className="btn" type="button">
												Cancel
											</button>
											<button disabled={isSubmitting} type="submit" className="btn btn-primary">
												{modalProps.type === 'create' ? 'Add Discount' : 'Save'}
											</button>
										</div>
									</div>
								</div>
							</form>
						)}
					/>
				</div>
				<div className="modal-backdrop show" tabIndex="1" />
			</Fragment>
		);
	}
}

const mapStateToProps = (state) => {
	const discountServicelinesData = getAccountDetails(state).billing.accountServicelines,
		currentLocationId = getUser(state).currentLocation.id,
		accountId = getAccountDetails(state).accountDetails.accountInfo.id,
		modalProps = getModalData(state).modalProps;

	return {
		discountServicelinesData,
		currentLocationId,
		accountId,
		modalProps,
	};
};

const mapDispatchToProps = {
	getAccountServicelines,
	createDiscount,
	updateDiscount,
	getServiceLineDiscounts,
	getAccountDiscount,
	hideModal,
	emptyAccountServicelines,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddAccountDiscountModal);